import React, { useMemo, useRef } from 'react';

import { getLanguage, getTimezone } from 'helpers/locale';
import { getRegisterId } from 'helpers/session';
import { usePayBalance } from 'root/apps/QuoteMachineIntegration/components/PayBalance';
import { useAutoARSettings } from 'root/apps/QuoteMachineIntegration/hooks/useAutoARSettings';
import { useCreditAccountBalanceDriftDetection } from 'root/apps/QuoteMachineIntegration/hooks/useCreditAccountBalanceDriftDetection';
import AutomatedAccountReceivablesService from 'root/apps/QuoteMachineIntegration/services/AutomatedAccountReceivablesService';
import { AutomatedAccountReceivablesManagementType } from 'root/apps/QuoteMachineIntegration/state/AutomatedAccountReceivablesManagementType';
import { AutomatedARManagementStatus } from 'root/apps/QuoteMachineIntegration/state/AutomatedARManagementStatus';
import { INVOICING_INTEGRATION_KEY } from 'root/apps/QuoteMachineIntegration/utils/referenceHelpers';

import {
  buildCreditAccountRef,
  useCustomerCreditAccountWebComponent,
} from './useCustomerProfileWebComponents';
import { getWebComponentCompatConfig } from './webComponentCompatConfig';

import type {
  CustomerCreditAccountWebComponentConfig,
  CustomerCreditAccountWebComponentHostProps,
} from './types';
import type { InvoicingActionRouterContext } from './useInvoicingActionRouter';

function mapAutomationStatus(status: AutomatedARManagementStatus): 'on' | 'off' | 'error' {
  if (status === AutomatedARManagementStatus.ON) return 'on';
  if (status === AutomatedARManagementStatus.ERROR) return 'error';
  return 'off';
}

export function CustomerCreditAccountWebComponentHost({
  customerID,
  accountID,
  customerFullName,
  customerEmail,
  currentCreditLimit,
  creditAccountID,
  isCustomerArchived,
  employeeHasPermissionsToEditCreditLimit,
  lsrBalanceOwed,
  lsrDepositBalance,
}: CustomerCreditAccountWebComponentHostProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [automationStatus, setAutomationStatus] = React.useState(AutomatedARManagementStatus.OFF);

  const {
    loading: loadingAutoArSettings,
    error: autoArError,
    accountStatus: autoArAccountStatus,
    customerStatus: autoArCustomerStatus,
  } = useAutoARSettings(creditAccountID.toString());

  const { mountPayBalance, unmountPayBalance } = usePayBalance({
    accountID,
    customerID,
    creditAccountID,
    customerName: customerFullName,
    customerEmail,
  });

  React.useEffect(() => {
    const fetchAutoARStatus = async () => {
      try {
        const res = await AutomatedAccountReceivablesService.getAutomatedAccountReceivablesStatus(
          AutomatedAccountReceivablesManagementType.CUSTOMER,
          creditAccountID.toString()
        );
        setAutomationStatus(res);
      } catch {
        setAutomationStatus(AutomatedARManagementStatus.ERROR);
      }
    };
    fetchAutoARStatus();
  }, [creditAccountID, customerID]);

  const wcConfig = useMemo((): CustomerCreditAccountWebComponentConfig | null => {
    if (loadingAutoArSettings) return null;

    return {
      ...getWebComponentCompatConfig(),
      baseUrl: `${window.location.origin}/admin/invoicing`,
      locale: getLanguage(),
      timezone: getTimezone(),
      customHeaders: {},
      integrationKey: INVOICING_INTEGRATION_KEY,
      accountId: accountID,
      customerId: customerID,
      creditAccountRef: buildCreditAccountRef(creditAccountID),
      creditAccountId: creditAccountID.toString(),
      customerFullName,
      customerEmail,
      currentCreditLimit: currentCreditLimit ?? '0',
      isCustomerArchived: Boolean(isCustomerArchived),
      employeeHasPermissionsToEditCreditLimit,
      registerId: getRegisterId(),
      showPayBalanceMountPoint: true,
      showAutomatedAccountReceivablesWidget: !loadingAutoArSettings && !autoArError,
      automatedAccountReceivablesOptions: {
        customerEnabled: autoArCustomerStatus,
        enabledByDefault: autoArAccountStatus,
      },
      automatedAccountReceivablesStatus: mapAutomationStatus(automationStatus),
      showCustomerProfileMissingEmailBanner: !customerEmail,
    };
  }, [
    accountID,
    autoArAccountStatus,
    autoArCustomerStatus,
    autoArError,
    automationStatus,
    creditAccountID,
    currentCreditLimit,
    customerEmail,
    customerFullName,
    customerID,
    employeeHasPermissionsToEditCreditLimit,
    isCustomerArchived,
    loadingAutoArSettings,
  ]);

  const actionContext = useMemo(
    (): InvoicingActionRouterContext => ({
      accountID,
      customerID,
      creditAccountID,
      autoArError: Boolean(autoArError),
    }),
    [accountID, autoArError, creditAccountID, customerID]
  );

  const payBalanceHandlers = useMemo(
    () => ({ mountPayBalance, unmountPayBalance }),
    [mountPayBalance, unmountPayBalance]
  );

  useCustomerCreditAccountWebComponent(containerRef, wcConfig, actionContext, payBalanceHandlers);

  useCreditAccountBalanceDriftDetection({
    creditAccountID: creditAccountID.toString(),
    currentCreditLimit,
    lsrBalanceOwed,
    lsrDepositBalance,
    enabled: Boolean(wcConfig),
  });

  return <div ref={containerRef} data-testid="customer-credit-account-wc-host" />;
}
